import {
  hasDiffuseTextureMap,
  resolveApiTextureColor,
  resolveZoneMaterialProps,
} from '@utils/sceneObjects/resolvePbrMaterial'
import { resolveFabricEnvMapIntensity, resolveFabricRoughness } from '@utils/sceneObjects/fabricTextureQuality'
import { isConservativeFabricTexture } from '@utils/sceneObjects/resolveTextureTiling'
import { MeshReflectorMaterial } from '@react-three/drei'
import { useAppStore } from '@store'
import { Item, Material, Textures, Zone } from '@types'
import { toVector3 } from '@utils/conversion/toVector3'
import { handleNodeMorphs, handleNodeTextures } from '@utils/sceneObjects/getZonesFromGLB'
import { isContiguousSurfaceZone } from '@utils/sceneObjects/isContiguousSurfaceZone'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { DoubleSide, FrontSide, Mesh, Texture } from 'three'

interface ContiguousFurnitureItem {
  id: string
  catalogID: string
  position: { x: number; y: number; z: number }
  width: number
  height: number
  thickness: number
  rotation: number
  orderIndex: number
  type: string
  description: string
}

interface ContiguousAnalysisResult {
  groups: ContiguousFurnitureItem[][]
  totalGroups: number
  totalItemsInGroups: number
}

type ObjectMeshProps = {
  node: Mesh & { name: string }
  zones: Zone
  morphs: Item['morphs']
  textures: Textures
  renderTarget?: Texture
  paginaPdf?: number | undefined
  itemHeight?: number
  itemWidth?: number
  itemThickness?: number
  getContiguousItems?: () => ContiguousAnalysisResult
  fatherId?: string
  itemId?: string
  universal?: boolean
}

export function ObjectMesh({
  node,
  zones,
  morphs,
  textures,
  renderTarget,
  paginaPdf,
  itemHeight = 0,
  itemWidth = 0,
  itemThickness = 0,
  getContiguousItems,
  fatherId,
  itemId,
  universal,
}: ObjectMeshProps) {
  const { name, uuid, ...rest } = node
  const isModalOpen = useAppStore.use.isModalOpen()
  const zoneValues = zones?.[name]?.material as Material
  const resolvedItemId = itemId ?? fatherId
  const zoneTexture = textures.find((texture) => texture.zoneName === name)
  const isFabric = zoneTexture
    ? isConservativeFabricTexture(zoneTexture, resolvedItemId)
    : false
  const repeatX = zoneValues?.repeatX
  const repeatY = zoneValues?.repeatY
  const textureRotation = zoneValues?.textureRotation
  const contadorModificaciones = useAppStore((s) => s.contadorModificaciones)
  const watchGlobalContador = isContiguousSurfaceZone(textures, name)

  const texturesKey = useMemo(
    () => textures.map((texture) => `${texture.zoneName}:${texture.url}`).join('|'),
    [textures],
  )
  const materialKey = zoneValues
    ? `${zoneValues.url}-${zoneValues.color}-${zoneValues.roughness}-${zoneValues.metalness}-${zoneValues.envMapIntensity}-${zoneValues.opacity}-${zoneValues.bumpMap}-${zoneValues.bumpScale}`
    : ''

  const [appliedTexture, setAppliedTexture] = useState<Texture | undefined>(
    () => node.material?.map ?? undefined,
  )

  const mapTexture = appliedTexture ?? node.material?.map
  const hasTextureMap = hasDiffuseTextureMap(zoneValues, {
    zoneName: name,
    textures,
    appliedMap: mapTexture,
  })
  const pbr = resolveZoneMaterialProps(zoneValues, hasTextureMap, {
    zoneName: name,
    textures,
    appliedMap: mapTexture,
  })
  const color = pbr.color
  const roughness = isFabric ? resolveFabricRoughness(pbr.roughness) : pbr.roughness
  const metalness = pbr.metalness
  const envMapIntensity = isFabric
    ? resolveFabricEnvMapIntensity(pbr.envMapIntensity)
    : pbr.envMapIntensity
  const opacity = pbr.opacity

  const syncAppliedTexture = () => {
    const currentMap = node.material?.map
    if (currentMap) {
      setAppliedTexture(currentMap)
    }
  }

  useLayoutEffect(() => {
    if (universal) return
    const zoneMaterial = zones?.[name]?.material
    const resolvedColor = resolveApiTextureColor(
      zoneMaterial?.color,
      hasDiffuseTextureMap(zoneMaterial, {
        zoneName: name,
        textures,
        appliedMap: node.material?.map,
      }),
    )
    if (typeof node.material?.color?.set === 'function') {
      node.material.color.set(resolvedColor)
    }
    node.material.needsUpdate = true
  }, [universal, zones, name, node.material, textures, texturesKey])

  useLayoutEffect(() => {
    handleNodeTextures(
      node,
      zones,
      textures,
      repeatX,
      repeatY,
      textureRotation,
      paginaPdf,
      itemHeight,
      itemWidth,
      itemThickness,
      getContiguousItems,
      fatherId,
      universal,
      resolvedItemId,
    )

    syncAppliedTexture()
  }, [
    node,
    zones,
    textures,
    texturesKey,
    materialKey,
    repeatX,
    repeatY,
    textureRotation,
    paginaPdf,
    itemHeight,
    itemWidth,
    itemThickness,
    getContiguousItems,
    fatherId,
    resolvedItemId,
    universal,
    ...(watchGlobalContador ? [contadorModificaciones] : []),
  ])

  useEffect(() => {
    syncAppliedTexture()
    const timeoutId = window.setTimeout(syncAppliedTexture, 100)
    return () => window.clearTimeout(timeoutId)
  }, [texturesKey, materialKey, node.material?.map])

  useMemo(() => {
    handleNodeMorphs(node, morphs)
  }, [morphs, node])

  const width = useMemo(() => morphs.find((el) => el.name === 'Ancho'), [morphs])

  const meshVisible = universal || Boolean(mapTexture)

  if (zoneValues?.mirror) {
    return (
      <mesh
        {...rest}
        visible={meshVisible}
        castShadow={opacity < 1 ? false : true}
        receiveShadow
        scale={toVector3([
          width?.min !== width?.max ? (width?.default ?? 1) / (width?.min ?? 1) : 1,
          1,
          1,
        ])}
      >
        <MeshReflectorMaterial
          key={`${zoneValues.url}-mesh-${uuid}-${zoneValues}`}
          mirror={1}
          resolution={1024}
          color={color}
          opacity={opacity}
          blur={[0, 0]}
          side={FrontSide}
          map={mapTexture}
          roughness={roughness}
          metalness={isModalOpen ? 1 : 0}
          envMap={renderTarget}
          depthWrite={opacity < 1 ? false : true}
          transparent={opacity < 1 ? true : false}
        />
      </mesh>
    )
  }

  return (
    <mesh
      key={`${zoneValues?.url}-mesh-${uuid}`}
      material={node.material}
      {...rest}
      visible={meshVisible}
      castShadow={opacity < 1 ? false : true}
      receiveShadow
      material-color={color}
      material-opacity={opacity}
      material-side={DoubleSide}
      material-map={mapTexture}
      material-roughness={roughness}
      material-metalness={metalness}
      material-envMapIntensity={envMapIntensity}
      material-envMap={envMapIntensity > 0 ? renderTarget : undefined}
      material-depthWrite={opacity >= 1}
      material-transparent={opacity < 1}
    />
  )
}
